import React, { useRef, useState, useEffect, Suspense } from 'react';
import { string, arrayOf, number, shape, func, bool, object, oneOf } from 'prop-types';
import { Box, IconButton, Typography } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Loading from 'components/common/Loading';
import { getBoxJoinAndPositionFromSpanElements } from 'components/@home/drawers/HrBotDrawer/pdf-utils';
import {
  selectPresentationSpansInCanvasRect,
  selectionRectCanvasClientFromOverlayGhost,
} from 'components/@home/drawers/HrBotDrawer/selection-utils';
import getFile from 'utils/getFile';
import getPdfjs from 'utils/pdfjs';
import { I18n } from 'utils/i18n';
import DocumentField from './DocumentField';
import DocumentRoutingItem from './DocumentRoutingItem';
import {
  buildPlacementField,
  DEFAULT_FIELD_SIZES,
  ROUTING_ITEM_GHOST,
} from './buildPlacementField';

const Document = React.lazy(() => import('components/common/PDFPreviewLazy/Document'));
const Page = React.lazy(() => import('components/common/PDFPreviewLazy/Page'));

const pdfStyle = {
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  alignItems: 'center',
};

const pageStyle = {
  width: 'fit-content',
  margin: '20px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  backgroundColor: '#fff',
  boxShadow:
    '0px 3px 5px -1px rgb(0 0 0 / 20%), 0px 5px 8px 0px rgb(0 0 0 / 14%), 0px 1px 14px 0px rgb(0 0 0 / 12%)',
  overflow: 'hidden',
  position: 'relative',
};

const PdfPreview = ({
  src,
  fields,
  onAddField,
  onUpdateField,
  onDeleteField,
  renderToolbar = null,
  routingItems = [],
  routingItemPreviewText = null,
  routingItemDetection = null,
  onUpdateRoutingItem = () => {},
  onDeleteRoutingItem = () => {},
  pendingPlacement = null,
  onPlace = null,
  onCancelPlacement = null,
  showSignerSelector = false,
  currentSignerIndex = 0,
  onSignerChange = () => {},
  members = [],
  signingMode = 'onePerMember',
  onSigningModeChange = () => {},
  showSigningMode = false,
}) => {
  const containerRef = useRef(null);
  const loadingRef = useRef(false);
  const currentSrcRef = useRef(null);
  const [data, setData] = useState(null);
  const [dimensions, setDimensions] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [ghost, setGhost] = useState(null);
  /**
   * Pdf.js page proxy used to render the text layer for routing-item selection. Captured from
   * `<Page onLoadSuccess>` and reset when the page changes so we don't render against a stale page.
   */
  const [pageProxy, setPageProxy] = useState(null);
  const textLayerRef = useRef(null);
  /** Same as `HrBotConfig`: `rectangleSelect` / trim use the **PDF canvas** as `referenceEl`, not the text-layer host. */
  const pdfCanvasRef = useRef(null);

  useEffect(() => {
    if (currentSrcRef.current !== src) {
      setData(null);
      setLoaded(false);
      loadingRef.current = false;
      currentSrcRef.current = src;
    }

    if (!loadingRef.current && src) {
      if (src instanceof Blob || src instanceof File) {
        setData(src);
      } else {
        loadingRef.current = true;
        getFile(src)
          .then(d => {
            if (currentSrcRef.current === src) {
              setData(d);
            }
            loadingRef.current = false;
          })
          .catch(e => {
            console.error('Error loading PDF:', e);
            if (currentSrcRef.current === src) {
              setLoaded(true);
            }
            loadingRef.current = false;
          });
      }
    }
  }, [src]);

  useEffect(() => {
    if (!pendingPlacement) {
      setGhost(null);
      return undefined;
    }
    const onKey = e => {
      if (e.key === 'Escape') {
        onCancelPlacement?.();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [pendingPlacement, onCancelPlacement]);

  useEffect(() => {
    if (!pendingPlacement || !dimensions) {
      return;
    }
    const scale = dimensions.width / dimensions.originalWidth;
    const { w, h } = (() => {
      if (pendingPlacement.kind === 'field') {
        const t = pendingPlacement.preset?.type || 'text';
        const s = DEFAULT_FIELD_SIZES[t] || DEFAULT_FIELD_SIZES.text;
        return { w: s.width * scale, h: s.height * scale };
      }
      if (pendingPlacement.kind === 'item') {
        return {
          w: ROUTING_ITEM_GHOST.width * scale,
          h: ROUTING_ITEM_GHOST.height * scale,
        };
      }
      return { w: 0, h: 0 };
    })();
    if (!w || !h) return;
    setGhost({
      x: Math.max(0, dimensions.width / 2 - w / 2),
      y: Math.max(0, dimensions.height / 2 - h / 2),
      w,
      h,
    });
  }, [pendingPlacement, dimensions]);

  /**
   * Render the pdf.js text layer manually (react-pdf's `renderTextLayer` is disabled because the
   * layer must stay below the field overlays). Used on routing-item **click** with the ghost rect
   * so `rectangleSelect` + `getBoxJoinAndPositionFromSpanElements` can set `position` / `join`.
   */
  useEffect(() => {
    let cancelled = false;
    let textLayerTask = null;
    (async () => {
      if (!pageProxy || !textLayerRef.current || !dimensions?.width) return;
      const pdfjs = await getPdfjs();
      if (cancelled || !textLayerRef.current) return;
      textLayerRef.current.innerHTML = '';
      const viewport = pageProxy.getViewport({
        scale: dimensions.width / dimensions.originalWidth,
        rotation: pageProxy.rotate,
      });
      const textContentSource = pageProxy.streamTextContent({ includeMarkedContent: true });
      textLayerTask = new pdfjs.TextLayer({
        container: textLayerRef.current,
        textContentSource,
        viewport,
      });
      try {
        await textLayerTask.render();
      } catch (e) {
        if (!cancelled && e?.name !== 'RenderingCancelledException') {
          console.warn('PDF text layer render', e);
        }
      }
    })();
    return () => {
      cancelled = true;
      if (textLayerTask?.cancel) textLayerTask.cancel();
    };
  }, [pageProxy, dimensions?.width, dimensions?.originalWidth]);

  const handleAddFieldFromToolbar = type => {
    if (!dimensions || !containerRef.current) return;
    const scrollTop = containerRef.current.scrollTop;
    const viewportHeight = containerRef.current.clientHeight;
    const field = buildPlacementField(type, {
      currentSignerIndex,
      currentPage,
      originalWidth: dimensions.originalWidth,
      originalHeight: dimensions.originalHeight,
      displayWidth: dimensions.width,
      displayHeight: dimensions.height,
      scrollTop,
      viewportHeight,
    });
    onAddField(field);
  };

  const handlePageChange = newPage => {
    setCurrentPage(newPage);
    setDimensions(null);
  };

  const currentPageFields = fields.filter(field => (field.page || 1) === currentPage);
  const currentPageRouting = (routingItems || []).filter(it => (it.page || 1) === currentPage);

  const ghostSize = () => {
    if (!pendingPlacement || !dimensions) return { w: 0, h: 0 };
    const scale = dimensions.width / dimensions.originalWidth;
    if (pendingPlacement.kind === 'field') {
      const t = pendingPlacement.preset?.type || 'text';
      const s = DEFAULT_FIELD_SIZES[t] || DEFAULT_FIELD_SIZES.text;
      return { w: s.width * scale, h: s.height * scale };
    }
    if (pendingPlacement.kind === 'item') {
      return {
        w: ROUTING_ITEM_GHOST.width * scale,
        h: ROUTING_ITEM_GHOST.height * scale,
      };
    }
    return { w: 0, h: 0 };
  };

  const handlePlacementMouseMove = e => {
    if (!pendingPlacement || !dimensions) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const { w, h } = ghostSize();
    setGhost({ x: x - w / 2, y: y - h / 2, w, h });
  };

  const handlePlacementClick = e => {
    if (!pendingPlacement || !dimensions || !onPlace) return;
    e.preventDefault();
    e.stopPropagation();
    const overlayEl = e.currentTarget;
    const rect = overlayEl.getBoundingClientRect();
    const offsetX = e.clientX - rect.left;
    const offsetY = e.clientY - rect.top;
    const { w, h } = ghostSize();
    const topLeftX = offsetX - w / 2;
    const topLeftY = offsetY - h / 2;

    if (pendingPlacement.kind === 'field') {
      onPlace({
        kind: 'field',
        preset: pendingPlacement.preset,
        page: currentPage,
        offsetX: topLeftX,
        offsetY: topLeftY,
        ghostWidth: w,
        ghostHeight: h,
        dimensions,
      });
      return;
    }

    if (pendingPlacement.kind === 'item') {
      // Uses the same helpers as `HrBotConfig` `handleMouseUp` — see `selection-utils.js`
      // `selectPresentationSpansInCanvasRect` (OMM-572).
      // Rect must match the **visible** dashed ghost (`ghost` from mousemove), not click-centred
      // math — otherwise a click slightly off the outline still selects under the pointer (e.g.
      // «NIF») while the UI shows the ghost over the id (OMM-572).
      const wUse = ghost?.w != null ? ghost.w : w;
      const hUse = ghost?.h != null ? ghost.h : h;
      const gx = ghost != null && Number.isFinite(ghost.x) ? ghost.x : offsetX - wUse / 2;
      const gy = ghost != null && Number.isFinite(ghost.y) ? ghost.y : offsetY - hUse / 2;
      const ghostOverlay = {
        left: gx,
        top: gy,
        right: gx + wUse,
        bottom: gy + hUse,
      };
      const scale = dimensions.width / dimensions.originalWidth;
      const coords = {
        left: ghostOverlay.left / scale,
        top: ghostOverlay.top / scale,
        right: ghostOverlay.right / scale,
        bottom: ghostOverlay.bottom / scale,
      };

      const textLayer = textLayerRef.current;
      const canvasEl = pdfCanvasRef.current;
      if (!textLayer) {
        onPlace({
          kind: 'item',
          preset: pendingPlacement.preset,
          page: currentPage,
          coords,
          position: 0,
          dimensions,
          recognitionWarning: 'noText',
        });
        return;
      }

      const overlayRect = overlayEl.getBoundingClientRect();
      const tlRect = textLayer.getBoundingClientRect();
      const selectionRect = canvasEl
        ? selectionRectCanvasClientFromOverlayGhost(canvasEl, overlayEl, ghostOverlay)
        : {
            left: ghostOverlay.left + (overlayRect.left - tlRect.left),
            top: ghostOverlay.top + (overlayRect.top - tlRect.top),
            right: ghostOverlay.right + (overlayRect.left - tlRect.left),
            bottom: ghostOverlay.bottom + (overlayRect.top - tlRect.top),
          };
      const refEl = canvasEl || textLayer;
      const selResult = selectPresentationSpansInCanvasRect(refEl, textLayer, selectionRect);

      if (selResult.multiline) {
        onPlace({
          kind: 'item',
          preset: pendingPlacement.preset,
          page: currentPage,
          coords,
          position: 0,
          dimensions,
          recognitionWarning: 'multiline',
        });
        return;
      }

      if (selResult.noHit || !selResult.selected?.length || !selResult.selectedText) {
        onPlace({
          kind: 'item',
          preset: pendingPlacement.preset,
          page: currentPage,
          coords,
          position: 0,
          dimensions,
          recognitionWarning: 'noText',
        });
        return;
      }

      const { selectedText, spansForPos } = selResult;

      const { join, pos } = getBoxJoinAndPositionFromSpanElements(spansForPos, selectedText);
      if (pos == null) {
        onPlace({
          kind: 'item',
          preset: pendingPlacement.preset,
          page: currentPage,
          coords,
          position: 0,
          dimensions,
          recognitionWarning: 'noText',
        });
        return;
      }

      onPlace({
        kind: 'item',
        preset: pendingPlacement.preset,
        page: currentPage,
        coords,
        position: pos,
        join: join || undefined,
        selectedText,
        dimensions,
      });
    }
  };

  const hasToolbar = typeof renderToolbar === 'function';
  const hasPagination = totalPages > 1;
  const pageControls = hasPagination ? (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1,
        py: 0.5,
        px: 1,
        flexShrink: 0,
      }}
    >
      <IconButton
        size="small"
        onClick={() => handlePageChange(currentPage - 1)}
        disabled={currentPage === 1}
        aria-label="Previous page"
      >
        <ChevronLeftIcon />
      </IconButton>
      <Typography sx={{ minWidth: 88, textAlign: 'center', fontSize: '0.875rem' }}>
        {I18n.t('Page')} {currentPage} / {totalPages}
      </Typography>
      <IconButton
        size="small"
        onClick={() => handlePageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        aria-label="Next page"
      >
        <ChevronRightIcon />
      </IconButton>
    </Box>
  ) : null;

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
      }}
    >
      {(hasToolbar || hasPagination) && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            width: '100%',
          }}
        >
          {hasToolbar ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                flexWrap: 'wrap',
                alignItems: 'center',
                flexGrow: 1,
              }}
            >
              {renderToolbar({
                onAddField: handleAddFieldFromToolbar,
                showSignerSelector,
                currentSignerIndex,
                onSignerChange,
                members,
                signingMode,
                onSigningModeChange,
                showSigningMode,
                fields,
                pageControls,
              })}
            </Box>
          ) : (
            hasPagination && pageControls
          )}
        </Box>
      )}

      <Box
        sx={theme => ({
          flex: 1,
          minHeight: 0,
          backgroundColor: '#f2f4f7',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          position: 'relative',
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 1,
          '&::after': {
            content: `''`,
            position: 'absolute',
            boxShadow: 'inset 0px 0px 10px rgba(0,0,0,0.1)',
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            pointerEvents: 'none',
          },
        })}
      >
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            position: 'relative',
            overflow: 'auto',
          }}
          ref={containerRef}
        >
          <Suspense fallback={<Loading sx={{ mt: '20px' }} size={60} />}>
            {data && (
              <Document
                file={data}
                style={pdfStyle}
                onLoadSuccess={pdf => {
                  setTotalPages(pdf.numPages);
                  setLoaded(true);
                }}
                loading={<Loading sx={{ mt: '20px' }} size={60} />}
              >
                {loaded && (
                  <Suspense fallback={<Loading sx={{ mt: '20px' }} size={60} />}>
                    <Page
                      canvasRef={pdfCanvasRef}
                      width={
                        containerRef.current?.offsetWidth
                          ? containerRef.current.offsetWidth - 40
                          : undefined
                      }
                      key={`page_${currentPage}`}
                      style={pageStyle}
                      pageNumber={currentPage}
                      onLoadSuccess={page => {
                        setPageProxy(page);
                        setDimensions({
                          width: page.width,
                          height: page.height,
                          originalWidth: page.originalWidth,
                          originalHeight: page.originalHeight,
                        });
                      }}
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                    >
                      <Box
                        ref={textLayerRef}
                        className="textLayer"
                        data-pdf-text-layer
                        sx={{
                          position: 'absolute',
                          inset: 0,
                          overflow: 'hidden',
                          lineHeight: 1,
                          zIndex: 0,
                          pointerEvents: 'none',
                          textAlign: 'initial',
                          transformOrigin: '0 0',
                          '& span': {
                            color: 'transparent',
                            position: 'absolute',
                            whiteSpace: 'pre',
                            margin: 0,
                            transformOrigin: '0 0',
                          },
                          '& span.markedContent': {
                            top: 0,
                            height: 0,
                          },
                        }}
                      />
                      {dimensions && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            zIndex: 1,
                            pointerEvents: 'none',
                            '& > *': {
                              pointerEvents: 'auto',
                            },
                          }}
                        >
                          {currentPageRouting.map(item => (
                            <DocumentRoutingItem
                              key={item.name}
                              item={item}
                              pdfDimensions={{
                                ...dimensions,
                                displayHeight: dimensions.height,
                                displayWidth: dimensions.width,
                              }}
                              extractedPreview={routingItemPreviewText?.[item.name]}
                              detectionStatus={routingItemDetection?.[item.name] ?? 'pending'}
                              onUpdate={onUpdateRoutingItem}
                              onDelete={onDeleteRoutingItem}
                            />
                          ))}
                          {currentPageFields.map(field => (
                            <DocumentField
                              key={field.id}
                              field={field}
                              containerRef={containerRef}
                              pdfDimensions={{
                                ...dimensions,
                                displayHeight: dimensions.height,
                                displayWidth: dimensions.width,
                              }}
                              onPositionChange={onUpdateField}
                              onUpdate={onUpdateField}
                              onDelete={onDeleteField}
                            />
                          ))}
                          {pendingPlacement && ghost && (
                            <Box
                              sx={theme => ({
                                position: 'absolute',
                                pointerEvents: 'none',
                                border: `2px dashed ${theme.palette.primary.main}`,
                                backgroundColor: 'rgba(25, 118, 210, 0.12)',
                                borderRadius: '4px',
                                zIndex: 4,
                                left: ghost.x,
                                top: ghost.y,
                                width: ghost.w,
                                height: ghost.h,
                              })}
                            />
                          )}
                          {pendingPlacement && (
                            <Box
                              role="presentation"
                              sx={{
                                position: 'absolute',
                                inset: 0,
                                zIndex: 3,
                                cursor: 'crosshair',
                                pointerEvents: 'auto',
                              }}
                              onMouseMove={handlePlacementMouseMove}
                              onClick={handlePlacementClick}
                            />
                          )}
                        </Box>
                      )}
                    </Page>
                  </Suspense>
                )}
              </Document>
            )}
          </Suspense>
        </Box>
      </Box>
    </Box>
  );
};

PdfPreview.propTypes = {
  src: string.isRequired,
  fields: arrayOf(
    shape({
      id: string.isRequired,
      type: string.isRequired,
      x: number.isRequired,
      y: number.isRequired,
      signerIndex: number.isRequired,
      page: number,
    }),
  ).isRequired,
  onAddField: func.isRequired,
  onUpdateField: func.isRequired,
  onDeleteField: func.isRequired,
  renderToolbar: func,
  routingItems: arrayOf(object),
  routingItemPreviewText: object,
  routingItemDetection: object,
  onUpdateRoutingItem: func,
  onDeleteRoutingItem: func,
  pendingPlacement: shape({
    kind: oneOf(['field', 'item']).isRequired,
    preset: object,
  }),
  onPlace: func,
  onCancelPlacement: func,
  showSignerSelector: bool,
  currentSignerIndex: number,
  onSignerChange: func,
  members: arrayOf(object),
  signingMode: oneOf(['onePerMember', 'oneForAll']),
  onSigningModeChange: func,
  showSigningMode: bool,
};

export default PdfPreview;
